import AdminService from "@/services/admin-service";
import { CalChannel } from "@/model/models";
import { feedWindowMs } from "@/utils/general-functions";

// Sources (cal_channel) logic for the Daily page: slot families, channel options, saving and removing sources. Mixed into Daily.vue: reads its state through `this`.
export default {
  computed: {
    // { [calMatchId]: CalChannel[] } - every persisted channel row for that match (active
    // and inactive alike, same as CalMatches.vue) - Sources panel is built from this.
    channelListByMatch() {
      const map = {};
      for (const ch of this.calChannelsList) {
        if (!map[ch.calMatchId]) map[ch.calMatchId] = [];
        map[ch.calMatchId].push(ch);
      }
      return map;
    },
    // { [slotNumber]: { base, b, c, d } } - see CalMatches.vue's own slotFamilies for the
    // full explanation. Built from channelOptions (every Channel row, application-agnostic).
    slotFamilies() {
      const families = {};
      for (const c of this.channelOptions) {
        const parsed = this.parseSlotChannelName(c.channelName);
        if (!parsed) continue;
        if (!families[parsed.number]) families[parsed.number] = {};
        families[parsed.number][parsed.letter || 'base'] = c;
      }
      return families;
    },
    // { [calMatchId]: CalChannel[] } - active-only, sorted - what the event<->channel
    // checkbox list is scoped to (never the global channel list). Same as MatchEvents.vue.
    channelsByMatch() {
      const map = {};
      for (const ch of this.calChannelsList) {
        if (!ch.active) continue;
        if (!map[ch.calMatchId]) map[ch.calMatchId] = [];
        map[ch.calMatchId].push(ch);
      }
      for (const list of Object.values(map)) {
        list.sort((a, b) => (a.channelName || '').localeCompare(b.channelName || ''));
      }
      return map;
    }
  },
  methods: {
    // --- Sources (channels) - copied from CalMatches.vue, unchanged behavior -----------
    parseSlotChannelName(channelName) {
      const m = /^stream_(\d{1,2})([b-d])?$/i.exec(channelName || '');
      if (!m) return null;
      return { number: Number(m[1]), letter: m[2] ? m[2].toLowerCase() : undefined };
    },
    // Same number-then-letter ordering as the slot families elsewhere (base < b < c < d),
    // rather than a plain string sort, which would put stream_10 before stream_2.
    compareChannelNames(a, b) {
      const pa = this.parseSlotChannelName(a);
      const pb = this.parseSlotChannelName(b);
      if (pa && pb) {
        if (pa.number !== pb.number) return pa.number - pb.number;
        return (pa.letter || '').localeCompare(pb.letter || '');
      }
      return a.localeCompare(b);
    },
    // Builds one ChannelUsageTimeline bar: match window + encoder-window "shadow", both
    // clamped to this day's 24h view (dayStart/dayEnd), as percentages. cutLeft/cutRight
    // (and the shadow's own) mark whichever edge got clamped, so the component can render a
    // flat edge instead of a rounded one - "this continues past what you're looking at".
    buildTimelineBar(channel, match) {
      const winStart = this.dayStart.getTime();
      const winEnd = this.dayEnd.getTime();
      const total = winEnd - winStart;
      const matchStart = new Date(match.startTime).getTime();
      const matchEnd = new Date(this.effectiveEnd(match)).getTime();
      const feed = feedWindowMs(channel, match);
      const rawShadowStart = feed ? feed.start : matchStart;
      const rawShadowEnd = feed ? feed.end : matchEnd;
      const shadowStart = Math.min(rawShadowStart, matchStart);
      const shadowEnd = Math.max(rawShadowEnd, matchEnd);
      const clampPct = (t) => ((Math.min(Math.max(t, winStart), winEnd) - winStart) / total) * 100;
      const barLeft = clampPct(matchStart);
      const barRight = clampPct(matchEnd);
      const shadowLeft = clampPct(shadowStart);
      const shadowRight = clampPct(shadowEnd);
      const label = `${match.team1} v ${match.team2}`;
      const tooltipLines = [
        label,
        `${this.formatLocalTime(match.startTime)}–${this.formatLocalTime(this.effectiveEnd(match))}`
      ];
      if (feed) {
        tooltipLines.push(`Encoder: ${this.formatLocalTime(new Date(feed.start).toISOString())}–${this.formatLocalTime(new Date(feed.end).toISOString())}`);
      }
      if (channel.origin) tooltipLines.push(`Origin: ${channel.origin}`);
      if (!channel.active) tooltipLines.push('Source inactive');
      // An active source no event uses yet: drawn amber so it reads as not publishing anywhere.
      const noEvent = channel.active && !this.eventLinkedCalChannelIds.has(channel.calChannelId);
      if (noEvent) tooltipLines.push('Not added to any event');
      return {
        calMatchId: match.calMatchId,
        left: barLeft,
        // 0 = the match itself is outside this day (lead-in only) - the component hides it.
        width: matchStart >= winEnd ? 0 : Math.max(barRight - barLeft, 0.4),
        shadowLeft,
        shadowWidth: Math.max(shadowRight - shadowLeft, 0.4),
        cutLeft: matchStart < winStart,
        cutRight: matchEnd > winEnd,
        shadowCutLeft: shadowStart < winStart,
        shadowCutRight: shadowEnd > winEnd,
        colorSlot: this.matchColorByMatchId[match.calMatchId],
        inactive: !channel.active,
        noEvent,
        label: noEvent ? `\u26A0 ${label} (not added to any event)` : label,
        tooltip: tooltipLines.join('\n')
      };
    },
    matchSlotNumber(calMatchId) {
      const channels = this.channelListByMatch[calMatchId] || [];
      for (const c of channels) {
        const parsed = this.parseSlotChannelName(c.channelName);
        if (parsed) return parsed.number;
      }
      return null;
    },
    baseChannelOptions() {
      return Object.keys(this.slotFamilies)
          .map(Number)
          .sort((a, b) => a - b)
          .filter(n => this.slotFamilies[n].base)
          .map(n => this.slotFamilies[n].base);
    },
    channelOptionsForRow(match, slot) {
      if (slot.channel) {
        const parsed = this.parseSlotChannelName(slot.channel.channelName);
        if (!parsed) return this.channelOptions;
        if (parsed.letter) {
          const family = this.slotFamilies[parsed.number];
          return family && family[parsed.letter] ? [family[parsed.letter]] : this.channelOptions;
        }
        return this.baseChannelOptions();
      }
      const establishedNumber = this.matchSlotNumber(match.calMatchId);
      if (establishedNumber === null) {
        return this.baseChannelOptions();
      }
      const family = this.slotFamilies[establishedNumber];
      if (!family) return this.channelOptions;
      const usedLetters = new Set(
          (this.channelListByMatch[match.calMatchId] || [])
              .map(c => this.parseSlotChannelName(c.channelName))
              .filter(Boolean)
              .map(p => p.letter || 'base')
      );
      const nextLetter = ['base', 'b', 'c', 'd'].find(l => !usedLetters.has(l) && family[l]);
      return nextLetter ? [family[nextLetter]] : [];
    },
    isSlotConfigured(slot) {
      return !!(slot && slot.channelName && slot.origin && slot.encoder && slot.encStreamId);
    },
    configuredCount(calMatchId) {
      const channels = this.channelListByMatch[calMatchId] || [];
      return channels.filter(c => c.active && this.isSlotConfigured(c)).length;
    },
    hasSourceConflict(calMatchId) {
      const channels = this.channelListByMatch[calMatchId] || [];
      return channels.some(c => !!this.channelConflicts[c.calChannelId]);
    },
    visibleMatchChannelRows(calMatchId) {
      const letterRank = { base: 0, b: 1, c: 2, d: 3 };
      return (this.channelListByMatch[calMatchId] || [])
          .slice()
          .sort((a, b) => {
            const pa = this.parseSlotChannelName(a.channelName);
            const pb = this.parseSlotChannelName(b.channelName);
            const ra = pa ? (letterRank[pa.letter || 'base'] ?? 4) : 4;
            const rb = pb ? (letterRank[pb.letter || 'base'] ?? 4) : 4;
            if (ra !== rb) return ra - rb;
            return (a.calChannelId || 0) - (b.calChannelId || 0);
          });
    },
    getVisibleSlotCount(calMatchId) {
      if (this.visibleSlotCount[calMatchId] !== undefined) {
        return this.visibleSlotCount[calMatchId];
      }
      return Math.max(this.visibleMatchChannelRows(calMatchId).length, 1);
    },
    visibleSlotsFor(match) {
      const channels = this.visibleMatchChannelRows(match.calMatchId);
      const visibleCount = this.getVisibleSlotCount(match.calMatchId);
      const slots = channels.map((ch, i) => ({ key: 'ch-' + ch.calChannelId, index: i + 1, channel: ch }));
      // A finished match shows only what was saved - no empty "Not set up" rows to fill in.
      if (!this.matchEditable(match)) return slots;
      for (let i = slots.length; i < visibleCount; i++) {
        slots.push({ key: 'new-' + i, index: i + 1, channel: null });
      }
      return slots;
    },
    addSourceSlot(calMatchId) {
      const current = this.getVisibleSlotCount(calMatchId);
      if (current < 4) {
        this.visibleSlotCount = { ...this.visibleSlotCount, [calMatchId]: current + 1 };
      }
    },
    isSourceLocked(match, slot) {
      if (slot.index <= 1) return false;
      const prior = this.visibleSlotsFor(match).find(s => s.index === slot.index - 1);
      return !this.isSlotConfigured(prior && prior.channel);
    },
    upsertChannel(calMatchId, payload) {
      const existing = payload.calChannelId
          ? this.calChannelsList.find(c => c.calChannelId === payload.calChannelId)
          : this.calChannelsList.find(c => c.calMatchId === calMatchId && c.channelName === payload.channelName);

      // The backend computes the window from the match: lead before its start, trail after its end (start when none).
      const leadMinutes = payload.leadMinutes !== undefined ? Math.abs(payload.leadMinutes || 0) : (existing ? existing.leadMinutes : undefined);
      const trailMinutes = payload.trailMinutes !== undefined ? Math.abs(payload.trailMinutes || 0) : (existing ? existing.trailMinutes : undefined);
      const calChannel = new CalChannel(
          existing ? existing.calChannelId : undefined,
          calMatchId,
          payload.encoder,
          payload.encStreamId,
          payload.encStreamName,
          payload.channelName,
          payload.origin,
          payload.orderId !== undefined ? payload.orderId : (existing ? existing.orderId : undefined),
          payload.language,
          leadMinutes,
          trailMinutes,
          existing ? existing.status : false,
          payload.active
      );
      return AdminService.upsertCalChannel(calChannel).then(() => calChannel);
    },
    refetchChannels() {
      const { from, to } = this.fetchWindow();
      return Promise.all([
        AdminService.getAllCalChannels(from, to),
        AdminService.getAllChannelConflicts(from, to)
      ]).then(([channels, channelConflicts]) => {
        this.calChannelsList = channels;
        const conflictMap = {};
        (channelConflicts || []).forEach((c) => { conflictMap[c.calChannelId] = c; });
        this.channelConflicts = conflictMap;
      });
    },
    saveSlot(calMatchId, payload) {
      return this.upsertChannel(calMatchId, payload).then((calChannel) => {
        return this.refetchChannels().then(() => calChannel);
      });
    },
    cascadeSlotFamilyChange(calMatchId, newNumber, savedCalChannelId) {
      const family = this.slotFamilies[newNumber];
      const siblings = this.visibleMatchChannelRows(calMatchId)
          // Active and inactive siblings alike - an inactive source stays visible, so it
          // has to follow the new family too, not be left pointing at the old number.
          .filter(c => c.calChannelId !== savedCalChannelId);
      const tasks = siblings.map((c) => {
        const parsed = this.parseSlotChannelName(c.channelName);
        const letter = parsed ? (parsed.letter || 'base') : null;
        const target = letter && letter !== 'base' && family ? family[letter] : null;
        return this.upsertChannel(calMatchId, {
          calChannelId: c.calChannelId,
          channelName: target ? target.channelName : c.channelName,
          origin: c.origin,
          encoder: c.encoder,
          encStreamId: c.encStreamId,
          encStreamName: c.encStreamName,
          language: c.language,
          leadMinutes: c.leadMinutes,
          trailMinutes: c.trailMinutes,
          // Moved: keeps its own active flag (an inactive source stays inactive).
          // No matching letter in the new family: deactivated, as before.
          active: target ? c.active : false
        });
      });
      return tasks.length ? Promise.allSettled(tasks) : Promise.resolve();
    },
    onMatchSlotSave(calMatchId, slotKey, payload) {
      const key = `${calMatchId}-${slotKey}`;
      const parsed = this.parseSlotChannelName(payload.channelName);
      const isFamilyNumberChange = parsed && !parsed.letter;
      const previousNumber = isFamilyNumberChange ? this.matchSlotNumber(calMatchId) : null;
      this.matchSlotSaving = { ...this.matchSlotSaving, [key]: true };
      this.matchSlotErrors = { ...this.matchSlotErrors, [key]: '' };
      this.saveSlot(calMatchId, payload).then((calChannel) => {
        if (isFamilyNumberChange && previousNumber !== null && previousNumber !== parsed.number) {
          return this.cascadeSlotFamilyChange(calMatchId, parsed.number, calChannel.calChannelId)
              .then(() => this.refetchChannels());
        }
      }).catch((error) => {
        this.matchSlotErrors = { ...this.matchSlotErrors, [key]: this.extractErrorMessage(error) };
      }).finally(() => {
        this.matchSlotSaving = { ...this.matchSlotSaving, [key]: false };
      });
    },
    // The "x"/Cancel on a still-empty, never-saved source slot (see CalChannelSlotEditor's
    // cancelEdit) - purely a client-side "one fewer slot" adjustment, nothing to send to the
    // BE. Saved sources have no remove: they're taken out of use with the Active checkbox
    // only, and always stay visible.
    onSourceRemove(calMatchId) {
      const previousCount = this.getVisibleSlotCount(calMatchId);
      this.visibleSlotCount = { ...this.visibleSlotCount, [calMatchId]: Math.max(previousCount - 1, 0) };
    }
  }
};
